import type { AddressModel, AddressModelFR } from './AddressModel';
import { translateAddressModel } from './AddressModel';
import type { LegalFormEnumFR } from './enums/LegalFormEnum';
import type { LegalPersonPartnerModel, LegalPersonPartnerModelFR } from './LegalPersonPartnerModel';
import { translateLegalPersonPartnerModelArray } from './LegalPersonPartnerModel';
import type { PhysicalPersonPartner, PhysicalPersonPartnerFR } from './PhysicalPersonPartner';
import { translatePhysicalPersonPartnerArray } from './PhysicalPersonPartner';
import type { DocumentsKYCModel, DocumentsKYCModelFR } from './DocumentsKYCModel';
import { translateDocumentKYC } from './DocumentsKYCModel';
import type { MaritalStatusEnumFR } from './enums/MaritalStatusEnum';
import type { ProfessionalSituationEnumFR } from './enums/ProfessionalSituationEnum';
import type { AccountAccesEspaceModel, AccountAccesEspaceResponse } from './AccountAccesEspace';
import { translateAccountAccesEspaceArrayResponse } from './AccountAccesEspace';
import type { ImageModel, ImageModelFR } from './ImageModel';
import { translateImageModel } from './ImageModel';
import type { NiveauEducationKYCEnum } from './enums/NiveauEducationKYCEnum';
import type { FileModel, FileModelFR } from '@app/models/api/FileModel';
import { translateFileModel } from '@app/models/api/FileModel';
import type { KYCStatusEnum } from '@app/enums/KYC';
import type { TypeAttestDom } from '@app/models/api/enums/TypeAttestDom';
import {
	type SignatureModel,
	type SignatureAPIModel,
	translateSignatureResponse
} from '@app/models/api/SignatureModel';
import type { FunctionOfTheLegalRepresentativeEnumFR } from './enums/FunctionOfTheLegalRepresentativeEnum';
import { AccountStateEnum } from '@wiseed/wikit';
import type { OperationRegulationsEnum } from './enums/OperationStatusEnum';
import type {
	EquityEnum,
	ResultEnum,
	RevenueEnum
} from '@app/models/api/enums/CapitalSegmentsOptions';

export type ExperiencesStatusModel = {
	basiquesOk: boolean;
	obligationsConvertiblesOk: boolean;
	opcOk: boolean;
	titresParticipatifsOk: boolean;
};

export type KnowledgeStatusModel = {
	basiquesOk: boolean;
	obligationsConvertiblesOk: boolean;
	opcOk: boolean;
	titresParticipatifsOk: boolean;
};
export enum adresseFiscaleEnum {
	MISSING = ' MISSING',
	EXPIRED = 'EXPIRED',
	UP_TO_DATE = 'UP_TO_DATE',
	EXPIRES_SOON = 'EXPIRES_SOON'
}

export type AccountStatus = {
	identiteOk: boolean;
	coordonneesOk: boolean;
	informationsPersonnellesOk: boolean;
	adresseFiscale: adresseFiscaleEnum;
	kyc: {
		psi: {
			statut: KYCStatusEnum;
		};
		psfp: {
			statut: KYCStatusEnum;
			financialDataStatut: KYCStatusEnum;
		};
	};
	walletOk: boolean;
	documentsOk: boolean;
	personneMoraleOk: boolean;
	dateBlocageSouscription: string;
	simulatorOk: boolean;
};

export enum UserRolesEnum {
	ROLE_SUPPORT = 'ROLE_SUPPORT',
	ROLE_ADMIN = 'ROLE_ADMIN'
}

export enum Action2FA {
	LOGIN = 'LOGIN',
	MONEY_OUT = 'MONEY_OUT'
}
export enum UserTrackingStatus {
	N1 = 'N1',
	N2 = 'N2',
	N3 = 'N3'
}

export type AccountStatusModel = {
	state: AccountStateEnum;
	iconName?: string;
	title?: string;
	ctaLink?: string;
	ctaLabel?: string;
};

export type AccountModel = {
	id: number;
	creationDate: string;
	email: string;
	status: AccountStatus;
	identity: AccountIdentityModel;
	signatureIdentityConfirmation: SignatureModel;
	coordinates: AccountContactModel;
	legalPerson: AccountLegalPersonModel;
	personalInformation: AccountPersonalInformations;
	balance: number;
	avatar: ImageModel;
	financialData: FinancialDataModel;
	documents: {
		id1: DocumentsKYCModel;
		dom: DocumentsKYCModel;
		kbis: DocumentsKYCModel;
		statuts: DocumentsKYCModel;
		annualAccounts: DocumentsKYCModel;
		cniTiers: DocumentsKYCModel;
		attestationTiers: DocumentsKYCModel;
		rbe: DocumentsKYCModel;
		proofOfResidenceType: TypeAttestDom;
	};
	matchingReport: FileModel;
	accesEspaces: AccountAccesEspaceResponse[];
	isPremium: boolean;
	isClientCGP: boolean;
	hasInvestmentFees: boolean;
	isPSFPInvestor: boolean;
	isPSIInvestor: boolean;
	isOPCInvestor: boolean;
	preferences: {
		communications: AccountPreferencesModelFR;
	};
	roles: UserRolesEnum[];
	auth2FA: Action2FA[];
	privateOperationID: number;
	KYCPreferenceType: OperationRegulationsEnum | null;
};

export interface AccountResponse {
	id: number;
	dateCreation: string;
	email: string;
	statuts: AccountStatus;
	identite: AccountIdentityModelFR;
	signatureConfirmationIdentite: SignatureAPIModel;
	coordonnees: AccountContactModelFR;
	personneMorale: AccountLegalPersonModelFR;
	informationsPersonnelles: AccountPersonalInformations;
	solde: number;
	photo: ImageModelFR;
	donneesFinancieres: FinancialDataModelFR;
	documents: {
		id1: DocumentsKYCModelFR;
		dom: DocumentsKYCModelFR;
		kbis: DocumentsKYCModelFR;
		statuts: DocumentsKYCModelFR;
		comptesAnnuels: DocumentsKYCModelFR;
		cniTiers: DocumentsKYCModelFR;
		attestationTiers: DocumentsKYCModelFR;
		typeAttestDom: TypeAttestDom;
		rbe: DocumentsKYCModelFR;
	};
	rapportAdequation: FileModelFR;
	accesEspaces: AccountAccesEspaceModel[];
	premium: boolean;
	clientCGP: boolean;
	hasInvestmentFees: boolean;
	investisseurPSFP: boolean;
	investisseurPSI: boolean;
	investisseurOPC: boolean;
	preferences: {
		communications: AccountPreferencesModelFR;
	};
	roles: UserRolesEnum[];
	auth2FA: Action2FA[];
	operationPriveeId: number;
	typeParcoursInscription: OperationRegulationsEnum | null;
}

export const translateAccountResponse = (ar: AccountResponse): AccountModel => {
	return {
		id: ar.id,
		creationDate: ar.dateCreation,
		email: ar.email,
		status: ar.statuts,
		identity: ar.identite && translateAccountIdentityModel(ar.identite),
		signatureIdentityConfirmation:
			ar.signatureConfirmationIdentite &&
			translateSignatureResponse(ar.signatureConfirmationIdentite),
		coordinates: ar.coordonnees && translateAccountContactModel(ar.coordonnees),
		legalPerson: ar.personneMorale && translateAccountLegalPersonModel(ar.personneMorale),
		personalInformation: ar.informationsPersonnelles,
		balance: ar.solde,
		avatar: ar.photo && translateImageModel(ar.photo),
		documents: ar.documents && {
			id1: ar.documents.id1 && translateDocumentKYC(ar.documents.id1),
			dom: ar.documents.dom && translateDocumentKYC(ar.documents.dom),
			kbis: ar.documents.kbis && translateDocumentKYC(ar.documents.kbis),
			statuts: ar.documents.statuts && translateDocumentKYC(ar.documents.statuts),
			cniTiers: ar.documents.cniTiers && translateDocumentKYC(ar.documents.cniTiers),
			attestationTiers:
				ar.documents.attestationTiers && translateDocumentKYC(ar.documents.attestationTiers),
			rbe: ar.documents.rbe && translateDocumentKYC(ar.documents.rbe),
			annualAccounts:
				ar.documents.comptesAnnuels && translateDocumentKYC(ar.documents.comptesAnnuels),
			proofOfResidenceType: ar.documents.typeAttestDom
		},
		financialData: translateFinancialData(ar.donneesFinancieres),
		matchingReport: ar.rapportAdequation && translateFileModel(ar.rapportAdequation),
		accesEspaces: ar.accesEspaces && translateAccountAccesEspaceArrayResponse(ar.accesEspaces),
		isPremium: ar.premium,
		isClientCGP: ar.clientCGP,
		isPSFPInvestor: ar.investisseurPSFP,
		isPSIInvestor: ar.investisseurPSI,
		isOPCInvestor: ar.investisseurOPC,
		hasInvestmentFees: ar.hasInvestmentFees,
		preferences: ar.preferences,
		roles: ar.roles,
		auth2FA: ar.auth2FA,
		privateOperationID: ar.operationPriveeId,
		KYCPreferenceType: ar.typeParcoursInscription
	};
};

type AccountIdentityModel = {
	civility?: string;
	lastName?: string;
	firstName?: string;
	commonName?: string;
	birthPlace?: string;
	countryOfBirth?: string;
	countyOfBirth?: string;
	dateOfBirth?: string;
	birthPlaceLabel?: string;
	postalCodeOfBirth?: string;
	nationality?: string;
	personType?: string;
};

type AccountIdentityModelFR = {
	civilite?: string;
	nomDeNaissance?: string;
	prenom?: string;
	nomDUsage?: string;
	lieuNaissance?: string;
	paysNaissance?: string;
	departementNaissance?: string;
	dateNaissance?: string;
	labelLieuNaissance?: string;
	codePostalNaissance?: string;
	nationalite?: string;
	typePersonne?: string;
};

type FinancialDataModelFR = {
	montantRevenusAnnuels: number;
	montantActifsLiquides: number;
	montantEngagementsFinanciers: number;
	recuResultatsSimulation: boolean;
	partageDonneesFinancieres: boolean;
};
type FinancialDataModel = {
	annualNetIncome: number;
	liquidAssetAmount: number;
	annualFinancialCommitmentAmount: number;
	receivedSimulationResult: boolean;
	financialDataSharing: boolean;
};

const translateAccountIdentityModel = (aI: AccountIdentityModelFR): AccountIdentityModel => {
	return {
		civility: aI?.civilite,
		lastName: aI?.nomDeNaissance,
		firstName: aI?.prenom,
		commonName: aI?.nomDUsage,
		birthPlace: aI?.lieuNaissance,
		countryOfBirth: aI?.paysNaissance,
		countyOfBirth: aI?.departementNaissance,
		dateOfBirth: aI?.dateNaissance,
		birthPlaceLabel: aI?.labelLieuNaissance,
		postalCodeOfBirth: aI?.codePostalNaissance,
		nationality: aI?.nationalite,
		personType: aI?.typePersonne
	};
};

type AccountPreferencesModelFR = {
	marketing?: boolean;
	smsInscription?: boolean;
	canaux?: AccountPreferencesCommCanauxModelFR;
	horaires?: AccountPreferencesCommHorairesModelFR;
};

type AccountPreferencesCommCanauxModelFR = {
	sms: boolean;
	email: boolean;
	telephone: boolean;
	whatsapp: boolean;
	reseauxSociaux: boolean;
};

type AccountPreferencesCommHorairesModelFR = {
	matin: boolean;
	apresMidi: boolean;
	soir: boolean;
};

type AccountContactModel = {
	fiscalAddress?: AddressModel;
	otherAddress?: AddressModel;
	phone?: string;
	phoneCode?: string;
};

type AccountContactModelFR = {
	adresseFiscale?: AddressModelFR;
	adresseDeCorrespondance?: AddressModelFR | null;
	telephone?: string;
	indicatif?: string;
};

const translateAccountContactModel = (ac: AccountContactModelFR): AccountContactModel => {
	return {
		fiscalAddress: ac.adresseFiscale && translateAddressModel(ac.adresseFiscale),
		otherAddress: ac.adresseDeCorrespondance && translateAddressModel(ac.adresseDeCorrespondance),
		phone: ac.telephone,
		phoneCode: ac.indicatif
	};
};

const translateFinancialData = (data: FinancialDataModelFR): FinancialDataModel => {
	return {
		annualNetIncome: data.montantRevenusAnnuels,
		liquidAssetAmount: data.montantActifsLiquides,
		annualFinancialCommitmentAmount: data.montantEngagementsFinanciers,
		receivedSimulationResult: data.recuResultatsSimulation,
		financialDataSharing: data.partageDonneesFinancieres
	};
};

export type AccountLegalPersonModel = {
	registrationDate?: string;
	fiscalAddress?: AddressModel;
	otherAddress?: AddressModel;
	nonTaxableVAT?: boolean;
	VATNumber?: string;
	name?: string;
	legalForm?: LegalFormEnumFR;
	registrationNumberSIREN?: string;
	registrationCity?: string;
	registrationCountry?: string;
	registrationNumberSIRET?: string;
	businessActivity?: string;
	businessTaxation?: string;
	functionOfTheLegalRepresentative?: FunctionOfTheLegalRepresentativeEnumFR;
	numberOfPartners?: number;
	businessResults?: { lastClosure: ResultEnum };
	businessRevenue?: { lastClosure: RevenueEnum };
	equity?: { lastClosure: EquityEnum };
	otherFunctionOfTheLegalRepresentative?: string;
	legalPersonPartners?: LegalPersonPartnerModel[];
	physicalPersonPartners?: PhysicalPersonPartner[];
};

type AccountLegalPersonModelFR = {
	registrationDate: string; // TODO Voir model
	adresseSiegeSocial?: AddressModelFR;
	adresseActiviteEffective?: AddressModelFR;
	nonAssujettiTVA?: boolean;
	numeroDeTva?: string;
	denomination?: string;
	formeJuridique?: LegalFormEnumFR;
	immatriculationNumero?: string;
	immatriculationVille?: string;
	immatriculationPays?: string;
	immatriculationSiret?: string;
	secteurActivite?: string;
	entrepriseImposition?: string;
	representantLegalFonction?: string;
	associes?: number;
	chiffreAffaire: {
		derniereCloture: RevenueEnum;
	};
	resultats: {
		derniereCloture: ResultEnum;
	};
	capitauxPropres: {
		derniereCloture: EquityEnum;
	};
	representantLegalFonctionAutre?: string;
	personnesMoralesAssociees?: LegalPersonPartnerModelFR[];
	personnesPhysiquesAssociees?: PhysicalPersonPartnerFR[];
};

export const translateAccountLegalPersonModel = (
	alp: AccountLegalPersonModelFR
): AccountLegalPersonModel => {
	return {
		fiscalAddress: alp.adresseSiegeSocial && translateAddressModel(alp.adresseSiegeSocial),
		otherAddress:
			alp.adresseActiviteEffective && translateAddressModel(alp.adresseActiviteEffective),
		nonTaxableVAT: alp.nonAssujettiTVA,
		VATNumber: alp.numeroDeTva,
		name: alp.denomination,
		legalForm: alp.formeJuridique,
		registrationNumberSIREN: alp.immatriculationNumero,
		registrationCity: alp.immatriculationVille,
		registrationCountry: alp.immatriculationPays,
		registrationNumberSIRET: alp.immatriculationSiret,
		businessActivity: alp.secteurActivite,
		businessTaxation: alp.entrepriseImposition,
		functionOfTheLegalRepresentative: alp.representantLegalFonction,
		numberOfPartners: alp.associes,
		otherFunctionOfTheLegalRepresentative: alp.representantLegalFonctionAutre,
		legalPersonPartners:
			alp.personnesMoralesAssociees &&
			translateLegalPersonPartnerModelArray(alp.personnesMoralesAssociees),
		physicalPersonPartners:
			alp.personnesPhysiquesAssociees &&
			translatePhysicalPersonPartnerArray(alp.personnesPhysiquesAssociees),
		businessResults: { lastClosure: alp.resultats.derniereCloture },
		businessRevenue: { lastClosure: alp.chiffreAffaire.derniereCloture },
		equity: { lastClosure: alp.capitauxPropres.derniereCloture }
	};
};

export type AccountPersonalInformations = {
	situationFamiliale: MaritalStatusEnumFR;
	situationPro: ProfessionalSituationEnumFR;
	secteurActiviteKYC: string;
	secteurActivite: string;
	ayantDroit: string;
	ppe: boolean;
	ppeFonctionExercee: string;
	ppePersonneConcernee: string;
	ppePaysExercice: string;
	profession: string;
	niveauEducation: NiveauEducationKYCEnum;
};
